import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  DollarSign, FileText, AlertTriangle, TrendingUp, Clock,
  CheckCircle, XCircle, ArrowRight, BarChart3
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import FormsInProgressWidget from '../../components/dashboard/FormsInProgressWidget';

const agingData = [
  { name: '0-30', value: 45000, fill: '#166534' },
  { name: '31-60', value: 28000, fill: '#92400E' },
  { name: '61-90', value: 15000, fill: '#991B1B' },
  { name: '90+', value: 8000, fill: '#64748B' },
];

const denialReasons = [
  { reason: 'Missing documentation', count: 12, amount: 4500 },
  { reason: 'Coding error', count: 8, amount: 3200 },
  { reason: 'Prior auth required', count: 6, amount: 2800 },
  { reason: 'Patient ineligible', count: 4, amount: 1500 },
];

const recentClaims = [
  { id: 'CLM-001', patient: 'Maria Chen', amount: 450, status: 'pending', date: '2024-01-15' },
  { id: 'CLM-002', patient: 'James Wilson', amount: 1200, status: 'denied', date: '2024-01-14' },
  { id: 'CLM-003', patient: 'Emily Rodriguez', amount: 320, status: 'paid', date: '2024-01-14' },
  { id: 'CLM-004', patient: 'Robert Thompson', amount: 890, status: 'pending', date: '2024-01-13' },
  { id: 'CLM-005', patient: 'Linda Davis', amount: 1550, status: 'paid', date: '2024-01-12' },
];

export default function BillingDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const kpis = [
    { title: 'Monthly Charges', value: '$125,400', icon: DollarSign, trend: '+8%', color: 'bg-[#0E7490]', link: '/claims' },
    { title: 'Collections Rate', value: '92%', icon: TrendingUp, trend: '+2%', color: 'bg-[#166534]', link: '/claims' },
    { title: 'Pending Claims', value: '47', icon: Clock, trend: '-5', color: 'bg-[#1A56DB]', link: '/claims' },
    { title: 'Denial Rate', value: '6.2%', icon: AlertTriangle, trend: '-0.5%', color: 'bg-[#92400E]', link: '/claims' },
  ];

  return (
    <div className="space-y-8" data-testid="billing-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Revenue Cycle Dashboard
        </h1>
        <p className="text-[#64748B] mt-1">
          Track claims, manage denials, and optimize revenue.
        </p>
      </div>

      {/* KPI Cards - Clickable Drill-downs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi, index) => (
          <Card key={index} className="careiq-metric-card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate(kpi.link)} data-testid={`drilldown-billing-${kpi.title.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className={`w-10 h-10 ${kpi.color} rounded-lg flex items-center justify-center`}>
                  <kpi.icon className="w-5 h-5 text-white" />
                </div>
                <span className={`text-sm ${kpi.trend.startsWith('+') || kpi.trend.startsWith('-') && kpi.title === 'Denial Rate' ? 'text-[#166534]' : 'text-[#991B1B]'}`}>
                  {kpi.trend}
                </span>
              </div>
              <div className="mt-3">
                <div className="text-2xl font-bold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {kpi.value}
                </div>
                <div className="text-xs text-[#64748B]">{kpi.title}</div>
              </div>
              <div className="mt-2 text-xs text-[#0E7490] flex items-center gap-1">View details <ArrowRight className="w-3 h-3" /></div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Forms In Progress */}
      <FormsInProgressWidget />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* AR Aging */}
        <Card className="careiq-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342]">AR Aging Buckets</CardTitle>
          </CardHeader>          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={agingData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tickFormatter={(v) => `$${v/1000}k`} stroke="#64748B" fontSize={12} />
                <YAxis type="category" dataKey="name" stroke="#64748B" fontSize={12} width={50} />
                <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="text-[#64748B]">Total Outstanding:</span>
              <span className="font-bold text-[#0A2342]">$96,000</span>
            </div>
          </CardContent>
        </Card>

        {/* Denial Management */}
        <Card className="careiq-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#0A2342]">Top Denial Reasons</CardTitle>
            <Link to="/denials" className="text-sm text-[#0E7490] hover:underline flex items-center gap-1">
              Manage <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {denialReasons.map((denial, index) => (
                <div key={index} className="flex items-center gap-4 p-3 rounded-lg bg-[#F8FAFC]">
                  <div className="flex-1">
                    <div className="font-medium text-[#0A2342]">{denial.reason}</div>
                    <div className="text-sm text-[#64748B]">{denial.count} claims • ${denial.amount.toLocaleString()}</div>
                  </div>
                  <Button size="sm" variant="outline" className="text-[#0E7490]">
                    Review
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Claims */}
      <Card className="careiq-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-[#0A2342]">Recent Claims</CardTitle>
          <Link to="/claims">
            <Button variant="outline" size="sm" className="careiq-btn-outline">
              View All Claims
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E2E8F0]">
                  <th className="table-header text-left">Claim ID</th>
                  <th className="table-header text-left">Patient</th>
                  <th className="table-header text-left">Amount</th>
                  <th className="table-header text-left">Status</th>
                  <th className="table-header text-left">Date</th>
                  <th className="table-header text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {recentClaims.map((claim) => (
                  <tr key={claim.id} className="table-row">
                    <td className="table-cell font-mono text-sm">{claim.id}</td>
                    <td className="table-cell">{claim.patient}</td>
                    <td className="table-cell font-medium">${claim.amount.toLocaleString()}</td>
                    <td className="table-cell">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        claim.status === 'paid' ? 'bg-[#166534]/10 text-[#166534]' :
                        claim.status === 'denied' ? 'bg-[#991B1B]/10 text-[#991B1B]' :
                        'bg-[#92400E]/10 text-[#92400E]'
                      }`}>
                        {claim.status === 'paid' && <CheckCircle className="w-3 h-3" />}
                        {claim.status === 'denied' && <XCircle className="w-3 h-3" />}
                        {claim.status === 'pending' && <Clock className="w-3 h-3" />}
                        {claim.status}
                      </span>
                    </td>
                    <td className="table-cell text-[#64748B]">{claim.date}</td>
                    <td className="table-cell">
                      {claim.status === 'denied' ? (
                        <Button size="sm" className="careiq-btn-secondary text-xs">
                          Appeal
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" className="text-[#0E7490]">
                          View
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
